import { useContext, useEffect, useState, memo } from "react";
import TimerIcon from "@mui/icons-material/Timer";
import TimerOffIcon from "@mui/icons-material/TimerOff";
import Tooltip from "@mui/material/Tooltip";

import { Context } from "../Provider";
import DivSpinner from "../../../components/divSpinner";
import ColumnSort from "./columnSort";
import { convertDateToUKFormat } from "../../../utils";
import FtmStages from "../../../components/ftmStages/FtmStages";
import { fetchStages } from "../../../components/ftmStages/service";

// --- Table Header ---
const TableHeader = memo(() => (
    <thead>
        <tr>
            {[
                "Tradesperson",
                "Contact Number",
                "Email",
                "Postcode",
                "Created At",
                "Stage",
                "Action",
            ].map((header) => (
                <th
                    key={header}
                    className="text-uppercase text-secondary text-sm font-weight-bolder opacity-7"
                >
                    <ColumnSort sortKeyName={header} />
                </th>
            ))}
        </tr>
    </thead>
));

// --- Single Row ---
const TableRow = memo(({ ftm, dispatch, stages }) => {
    const {
        ftm_profile_image_path,
        ftm_forename,
        ftm_surname,
        ftm_id,
        ftm_contact_number,
        ftm_email,
        ftm_postcode,
        ftm_createdAt,
        ftm_stage_id,
        ftm_last_review_hours,
    } = ftm;

    return (
        <tr
            className="cursor-pointer table-hover-row"
            onClick={() => dispatch({ type: "SELECTED_FTM", selected_ftm: ftm })}
        >
            <td>
                <div className="d-flex px-2 py-1">
                    <img
                        src={ftm_profile_image_path || "/assets/img/lock_user.png"}
                        className="avatar avatar-sm me-3 border-radius-lg"
                        alt="profile"
                    />
                    <div className="d-flex flex-column justify-content-center">
                        <h6 className="mb-0 text-sm">
                            {ftm_forename} {ftm_surname}
                        </h6>
                        <p className="text-xs text-secondary mb-0">#FTM ID {ftm_id}</p>
                    </div>
                </div>
            </td>
            <td className="text-secondary text-sm font-weight-bold">
                {ftm_contact_number}
            </td>
            <td className="text-secondary text-sm font-weight-bold">{ftm_email}</td>
            <td className="text-secondary text-sm font-weight-bold">
                {ftm_postcode || "N/A"}
            </td>
            <td className="text-secondary text-sm font-weight-bold">
                {convertDateToUKFormat(ftm_createdAt)}
            </td>
            <td className="text-center">
                <FtmStages currentStage={ftm_stage_id} stagesList={stages} />
            </td>
            <td className="text-center">
                {ftm_last_review_hours > 24 ? (
                    <Tooltip title="Application not reviewed by Administrator" arrow>
                        <TimerOffIcon color="error" />
                    </Tooltip>
                ) : (
                    <Tooltip title="Application reviewed by Administrator" arrow>
                        <TimerIcon style={{ color: "green" }} />
                    </Tooltip>
                )}
            </td>
        </tr>
    );
});

// --- No Data ---
const NoDataAvailable = memo(() => (
    <tr>
        <td colSpan="7" className="text-center">
            <span className="text-secondary text-sm font-weight-bold">
                No data available
            </span>
        </td>
    </tr>
));

const Table = () => {
    const { state, dispatch } = useContext(Context);
    const { is_loading, ftms } = state;

    const [stages, setStages] = useState([]);

    useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                const response = await fetchStages();
                if (isMounted) setStages(response.stages);
            } catch (error) {
                console.error("Failed to fetch stages:", error);
            }
        })();
        return () => {
            isMounted = false;
        };
    }, []);

    if (is_loading) {
        return (
            <div style={{ height: `${window.innerHeight - 387}px` }}>
                <DivSpinner />
            </div>
        );
    }

    return (
        <div className="table-responsive p-0">
            <table className="table align-items-center mb-0">
                <TableHeader />
                <tbody>
                    {ftms.length ? (
                        ftms.map((ftm) => (
                            <TableRow
                                key={ftm.ftm_id}
                                ftm={ftm}
                                dispatch={dispatch}
                                stages={stages}
                            />
                        ))
                    ) : (
                        <NoDataAvailable />
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default Table;